import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import Navbar from '../components/Navbar';
import NearbyModal from '../components/NearbyModal';
import useIsMobile from '../hooks/useIsMobile';

const DIETARY = [
  { id: 'vegetarian', label: 'Vegetarian', desc: 'No meat or fish' },
  { id: 'vegan', label: 'Vegan', desc: 'No animal products' },
  { id: 'keto', label: 'Keto', desc: 'Low carb (under 10g)' },
  { id: 'halal', label: 'Halal', desc: 'Halal certified' },
  { id: 'kosher', label: 'Kosher', desc: 'Kosher dietary laws' },
  { id: 'pescatarian', label: 'Pescatarian', desc: 'Fish but no other meat' },
];

const s = {
  page: { minHeight: '100vh', background: '#ffffff' },
  hero: { padding: '5rem 2rem 3rem', textAlign: 'center', maxWidth: '700px', margin: '0 auto' },
  h1: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif', fontWeight: 800,
    fontSize: 'clamp(1.8rem, 4vw, 3.2rem)',
    lineHeight: 1.15, marginBottom: '1rem',
    letterSpacing: '-0.03em', overflowWrap: 'break-word', wordBreak: 'break-word',
  },
  grad: { background: 'linear-gradient(135deg, #3a7d00, #1f4500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  sub: { color: '#666680', fontSize: '1.1rem', marginBottom: '2.5rem', lineHeight: 1.6 },
  actions: { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' },
  btnPrimary: {
    padding: '0.85rem 2rem', borderRadius: '12px',
    background: 'linear-gradient(135deg, #3a7d00, #1f4500)',
    color: '#fff', fontSize: '1rem', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', cursor: 'pointer',
  },
  btnGhost: {
    padding: '0.85rem 2rem', borderRadius: '12px',
    background: 'transparent', border: '1px solid rgba(0,0,0,0.15)',
    color: '#111111', fontSize: '1rem', fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer',
  },
  section: { maxWidth: '1100px', margin: '0 auto', padding: '2rem' },
  sectionTitle: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '1.4rem', marginBottom: '1.5rem', color: '#111111' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' },
  card: {
    background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px',
    padding: '1.5rem 1rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
  },
  cardEmoji: { fontSize: '2.5rem', lineHeight: 1 },
  cardName: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#111111' },
  cardLink: { color: '#3a7d00', fontSize: '0.85rem', fontWeight: 500 },
  loadingGrid: { textAlign: 'center', padding: '4rem', color: '#666680' },
  modal: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  },
  modalBox: {
    background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '80vh', overflowY: 'auto',
  },
  modalTitle: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '1.3rem', marginBottom: '0.5rem', color: '#111111' },
  modalSub: { color: '#666680', fontSize: '0.9rem', marginBottom: '1.5rem' },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0', borderBottom: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer' },
  checkbox: { width: '18px', height: '18px', accentColor: '#3a7d00', marginTop: '2px' },
  checkLabel: { fontWeight: 500, fontSize: '0.95rem', color: '#111111' },
  checkDesc: { color: '#666680', fontSize: '0.8rem' },
  macroGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1.25rem' },
  macroLabel: { color: '#666680', fontSize: '0.8rem', marginBottom: '0.35rem', fontWeight: 500 },
  macroInput: {
    width: '100%', padding: '0.6rem 0.75rem',
    background: '#f0f0f0', border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '8px', color: '#111111', fontSize: '0.9rem', outline: 'none',
  },
  modalActions: { display: 'flex', gap: '0.75rem', marginTop: '1.5rem' },
  applyBtn: { flex: 1, padding: '0.75rem', borderRadius: '10px', background: 'linear-gradient(135deg, #3a7d00, #1f4500)', color: '#fff', fontWeight: 600, fontSize: '0.95rem', border: 'none', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '0.75rem', borderRadius: '10px', background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', color: '#666680', fontWeight: 500, fontSize: '0.95rem', cursor: 'pointer' },
};

const TAGLINES = [
  "Craving something specific?",
  "Eat what you love. Hit your goals.",
  "The burger and the gains.",
  "Fast food, finally on your side.",
  "Find the protein. Skip the guilt.",
  "Your macros. Your rules.",
  "Order smarter, not smaller.",
  "Because you deserve both.",
  "Fuel up without the guesswork.",
  "Know before you order.",
];

export default function Home({ user }) {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const [dietary, setDietary] = useState([]);
  const [macros, setMacros] = useState({ maxCal: '', minProtein: '', maxCarbs: '', maxFat: '' });
  const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);

  useEffect(() => {
    const fetchRestaurants = async () => {
      setLoadingRestaurants(true);
      const { data } = await supabase
        .from('restaurants')
        .select('name, emoji, color')
        .order('name');
      setRestaurants(data || []);
      setLoadingRestaurants(false);
    };
    fetchRestaurants();
  }, []);

  const isMobile = useIsMobile();
  const toggleDiet = (id) => setDietary(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div style={s.page}>
      <Navbar user={user} />
      <div style={{ ...s.hero, ...(isMobile ? { padding: '3rem 1.25rem 2rem' } : {}) }}>
        <h1 style={s.h1}><span style={s.grad}>{tagline}</span></h1>
        <p style={s.sub}>Select a restaurant or find options near you to start optimizing your macros.</p>
        <div style={{ ...s.actions, ...(isMobile ? { flexDirection: 'column', alignItems: 'stretch' } : {}) }}>
          <button style={{ ...s.btnPrimary, ...(isMobile ? { justifyContent: 'center' } : {}) }} onClick={() => setShowPrefs(true)}>⚙️ Set Dietary Preferences</button>
          <button style={{ ...s.btnGhost, ...(isMobile ? { justifyContent: 'center' } : {}) }} onClick={() => setShowNearby(true)}>📍 Find Restaurants Near Me</button>
        </div>
      </div>

      <div style={{ ...s.section, ...(isMobile ? { padding: '1rem' } : {}) }}>
        <div style={s.sectionTitle}>Choose a Restaurant</div>
        {loadingRestaurants ? (
          <div style={s.loadingGrid}>Loading restaurants...</div>
        ) : (
          <div style={{ ...s.grid, ...(isMobile ? { gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' } : {}) }}>
            {restaurants.map(r => (
              <div key={r.name} style={{ ...s.card, ...(isMobile ? { padding: '1rem 0.5rem' } : {}) }}
                onClick={() => navigate(`/restaurant/${encodeURIComponent(r.name)}`)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = r.color; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ ...s.cardEmoji, ...(isMobile ? { fontSize: '2rem' } : {}) }}>{r.emoji}</div>
                <div style={{ ...s.cardName, ...(isMobile ? { fontSize: '0.8rem' } : {}) }}>{r.name}</div>
                <div style={{ ...s.cardLink, ...(isMobile ? { fontSize: '0.72rem' } : {}) }}>Start Optimizing →</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showNearby && <NearbyModal onClose={() => setShowNearby(false)} />}

      {showPrefs && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setShowPrefs(false)}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>⚙️ Dietary Preferences</div>
            <div style={s.modalSub}>Set your preferences once and they'll apply to all restaurants.</div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#3a7d00', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Dietary Restrictions</div>
            {DIETARY.map(d => (
              <div key={d.id} style={s.checkRow} onClick={() => toggleDiet(d.id)}>
                <input type="checkbox" style={s.checkbox} checked={dietary.includes(d.id)} onChange={() => toggleDiet(d.id)} />
                <div>
                  <div style={s.checkLabel}>{d.label}</div>
                  <div style={s.checkDesc}>{d.desc}</div>
                </div>
              </div>
            ))}
            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#3a7d00', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '1.5rem', marginBottom: '0.5rem' }}>Macro Goals (Optional)</div>
            <div style={s.macroGrid}>
              {[['maxCal','Max Calories','e.g. 600'],['minProtein','Min Protein (g)','e.g. 30'],['maxCarbs','Max Carbs (g)','e.g. 50'],['maxFat','Max Fat (g)','e.g. 25']].map(([key, label, ph]) => (
                <div key={key}>
                  <div style={s.macroLabel}>{label}</div>
                  <input style={s.macroInput} placeholder={ph} value={macros[key]} onChange={e => setMacros(p => ({...p, [key]: e.target.value}))} />
                </div>
              ))}
            </div>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setShowPrefs(false)}>Cancel</button>
              <button style={s.applyBtn} onClick={() => setShowPrefs(false)}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
